import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, AlertTriangle, Activity, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, absUrl, STEM_ORDER, authedUrl } from "@/lib/api";
import { fmtDuration, fmtRate, fmtSecs, fmtCost, fmtDate, timecode } from "@/lib/format";
import { useSyncedPlayer } from "@/components/qc/useSyncedPlayer";
import { StemLane } from "@/components/qc/StemLane";
import { TransportBar } from "@/components/qc/TransportBar";
import { QCPanel } from "@/components/qc/QCPanel";
import { StatusBadge } from "@/components/common/StatusBadge";

const JobPicker = () => {
  const [jobs, setJobs] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/jobs?status=completed").then((r) => setJobs(r.data)); }, []);
  return (
    <div data-testid="qc-job-picker" className="space-y-4 fade-up">
      <h1 className="font-display text-2xl font-bold tracking-tight">QC Console</h1>
      <p className="text-sm text-slate-400">Select a completed job to review its stems.</p>
      <div className="panel divide-y divide-[#1c2433]">
        {jobs.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No completed jobs yet.</div>}
        {jobs.map((j) => (
          <button key={j.id} data-testid={`qc-pick-job-${j.id}`} onClick={() => nav(`/qc/${j.id}`)} className="w-full text-left px-4 py-3 row-hover flex items-center gap-4">
            <span className="font-medium flex-1 truncate">{j.source_filename}</span>
            <span className="font-mono text-xs text-slate-400">{j.preset} · {j.engine_name}</span>
            <StatusBadge status={j.qc_status} />
          </button>
        ))}
      </div>
    </div>
  );
};

const Meta = ({ label, value, testId }) => (
  <div data-testid={testId}><div className="micro">{label}</div><div className="font-mono text-xs text-slate-200 mt-0.5 truncate" title={String(value)}>{value}</div></div>
);

const Hotspots = ({ job, player, onAnalyzed }) => {
  const [busy, setBusy] = useState(false);
  const a = job.analysis || {};
  const run = async () => {
    setBusy(true);
    try { await api.post(`/jobs/${job.id}/analyze`); toast.success("Leakage heatmap computed"); onAnalyzed(); } catch (e) { toast.error("Analysis failed"); } finally { setBusy(false); }
  };
  if (!a.bins) {
    return (
      <div data-testid="qc-hotspots-empty" className="panel px-4 py-3 flex items-center justify-between text-xs text-slate-400">
        <span>No leakage heatmap for this job yet.</span>
        <button data-testid="qc-analyze-button" disabled={busy} onClick={run} className="px-3 h-8 rounded-sm border border-[#3A4A68] hover:bg-[#1E2738] text-slate-200 disabled:opacity-50 flex items-center gap-2"><Activity size={13} /> {busy ? "Analysing…" : "Compute heatmap"}</button>
      </div>
    );
  }
  const spots = a.hotspots || [];
  return (
    <div data-testid="qc-hotspots" className="panel px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="micro flex items-center gap-2"><Activity size={11} className="text-rose-400" /> Leakage hotspots · {spots.length} flagged · heuristic, always confirm by ear</div>
        <button data-testid="qc-reanalyze-button" disabled={busy} onClick={run} className="text-[11px] text-slate-500 hover:text-white">{busy ? "recomputing…" : "recompute"}</button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {spots.length === 0 && <span className="text-xs text-emerald-400">No strong dialogue signature detected in the M&E stem.</span>}
        {spots.map((h, i) => (
          <button key={i} data-testid={`qc-hotspot-${i}`} onClick={() => player.seek(h.t)}
            className="font-mono text-xs px-2 h-7 rounded-sm border border-rose-800 bg-rose-500/10 text-rose-200 hover:bg-rose-500/25 flex items-center gap-2">
            {timecode(h.t)} <span className="text-rose-400/80">{Math.round(h.score * 100)}%</span>
          </button>
        ))}
      </div>
    </div>
  );
};

function QCView({ jobId }) {
  const [job, setJob] = useState(null);
  const load = useCallback(() => api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)), [jobId]);
  useEffect(() => { load(); }, [load]);

  const files = useMemo(() => {
    if (!job) return {};
    return { original: job.original_file, ...(job.output_files || {}) };
  }, [job]);
  const tracks = useMemo(() => STEM_ORDER.filter((k) => files[k]).map((k) => ({ key: k, url: absUrl(files[k].stream_url) })), [files]);
  const player = useSyncedPlayer(tracks);

  if (!job) return <div className="text-slate-500 text-sm p-8">Loading job…</div>;
  const media = job.media || {};
  return (
    <div data-testid="qc-interface-screen" className="space-y-4 fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold tracking-tight truncate max-w-[640px]">{job.source_filename}</h1>
            <StatusBadge status={job.status} />
          </div>
          <p className="text-sm text-slate-400 mt-1">{job.engine_name} · {job.model_name} {job.model_version} · preset {job.preset}</p>
          {job.engine === "mock" && (
            <div data-testid="qc-mock-warning" className="mt-2 inline-flex items-center gap-2 text-xs text-amber-300 bg-amber-400/10 border border-amber-700 rounded-sm px-2.5 py-1">
              <AlertTriangle size={13} /> MOCK engine output — frequency-band placeholder stems, not real separation.
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <a data-testid="qc-report-pdf-button" href={authedUrl(`/jobs/${job.id}/report.pdf`)} className="flex items-center gap-2 px-3 h-9 rounded-sm bg-[#1E2738] hover:bg-[#253044] border border-[#3A4A68] text-sm">
            <FileText size={14} /> QC report (PDF)
          </a>
          <a data-testid="download-wav-stems-button" href={authedUrl(`/jobs/${job.id}/download-all`)} className="flex items-center gap-2 px-3 h-9 rounded-sm bg-[#1E2738] hover:bg-[#253044] border border-[#3A4A68] text-sm">
            <Download size={14} /> Download all WAV stems
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-2">
          {STEM_ORDER.map((k) => (
            <StemLane key={k} stem={k} file={files[k]} player={player} analysis={job.analysis}
              missing={k !== "original" && job.requested_stems.includes(k) && !files[k] ? "engine returned no file" : undefined} />
          ))}
          <Hotspots job={job} player={player} onAnalyzed={load} />
        </div>
        <div className="space-y-4">
          <QCPanel job={job} onUpdated={load} currentTime={player.time} />
          <div className="panel p-4 grid grid-cols-2 gap-3">
            <Meta label="Engine / model" value={`${job.engine_name} · ${job.model_name || ""}`} testId="qc-meta-engine" />
            <Meta label="Submitted by" value={job.operator_name || job.operator_id} testId="qc-meta-operator" />
            <Meta label="QC reviewed by" value={job.qc_reviewed_by ? `${job.qc_reviewed_by} · ${fmtDate(job.qc_reviewed_at)}` : "—"} testId="qc-meta-reviewer" />
            <Meta label="Project / Episode" value={`${job.project_id || "—"} / ${job.episode_id || "—"}`} testId="qc-meta-project" />
            <Meta label="Source duration" value={fmtDuration(job.source_duration)} testId="qc-meta-duration" />
            <Meta label="Source format" value={`${String(job.source_codec).toUpperCase()} · ${fmtRate(job.source_sample_rate)} · ${job.source_channels}ch`} testId="qc-meta-format" />
            <Meta label="Source bit depth" value={media.source_bit_depth ? `${media.source_bit_depth}-bit` : "n/a"} testId="qc-meta-bitdepth" />
            <Meta label="Processing time" value={fmtSecs(job.processing_duration_sec)} testId="qc-meta-proctime" />
            <Meta label="Cost" value={fmtCost(job.processing_cost, job.cost_unit)} testId="qc-meta-cost" />
            <Meta label="Started" value={fmtDate(job.processing_start)} testId="qc-meta-start" />
            <Meta label="Finished" value={fmtDate(job.processing_end)} testId="qc-meta-end" />
            <Meta label="External job" value={job.external_job_id || "—"} testId="qc-meta-external" />
            <Meta label="Job ID" value={job.id} testId="qc-meta-jobid" />
          </div>
          {job.error_logs?.length > 0 && (
            <div data-testid="qc-error-logs" className="panel p-4 text-xs space-y-1">
              <div className="micro text-rose-400">Error log</div>
              {job.error_logs.map((e, i) => <div key={i} className="font-mono text-rose-300">{e.message}</div>)}
            </div>
          )}
        </div>
      </div>
      <TransportBar player={player} />
    </div>
  );
}

export default function QCConsole() {
  const { jobId } = useParams();
  return jobId ? <QCView key={jobId} jobId={jobId} /> : <JobPicker />;
}
